<template>
  <ManageShell v-model="dataControl" disableRegister disableDelete disableEdit disableExport>
    <template #title-buttons="{meta}">
      <div v-if="meta?.linkedSeller?.name" class="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600">
        <span class="font-semibold text-slate-800">{{ meta.linkedSeller.name }}</span>
        <span class="ml-2">{{ linkedSellerCaption }}</span>
      </div>
    </template>

    <template #middle-card="{meta, items}">
      <div class="space-y-2">
        <div class="rounded-[16px] border border-slate-200 bg-white px-4 py-4">
          <div class="grid gap-4 lg:grid-cols-[minmax(280px,360px)_minmax(0,1fr)] lg:items-end">
            <div>
              <div class="goal-panel-caption">Meta vigente no período</div>
              <Select
                :model-value="selectedGoalModel(meta?.goals)"
                :items="goalSelectItems(meta?.goals)"
                placeholder="Nenhuma meta vigente no período"
                item-value="value"
                item-text="label"
                :disabled="!goalSelectItems(meta?.goals).length"
                @update:model-value="selectedGoalId = $event" />
            </div>

            <div class="min-w-0">
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div class="min-w-0">
                  <div class="goal-panel-title">{{ activeGoal(meta?.goals)?.metricName || activeGoal(meta?.goals)?.title || "Nenhuma meta carregada" }}</div>
                  <div class="goal-panel-subtitle">{{ goalRuleSummary(activeGoal(meta?.goals)) }}</div>
                </div>

                <div class="text-right">
                  <div v-if="isGoalCompleted(activeGoal(meta?.goals))" class="goal-hit-badge">
                    <i class="fa-solid fa-circle-check"></i>
                    <span>META BATIDA!</span>
                  </div>
                  <div class="goal-panel-percent">{{ formatGoalPercent(activeGoal(meta?.goals)?.percentage) }}</div>
                  <div class="goal-panel-values">
                    {{ formatMoney(activeGoal(meta?.goals)?.actualMoney) }} / {{ formatMoney(activeGoal(meta?.goals)?.targetMoney) }}
                  </div>
                </div>
              </div>

              <div class="mt-3">
                <ProgressBar
                  :key="goalProgressKey(activeGoal(meta?.goals))"
                  :value="goalProgressValue(activeGoal(meta?.goals))"
                  :max="goalProgressMax(activeGoal(meta?.goals))"
                  :fill-duration="1250"
                  :color="goalProgressColor(activeGoal(meta?.goals))"
                  celebrate />
              </div>
            </div>
          </div>
        </div>

        <div class="overflow-x-auto pb-1">
          <div class="grid min-w-[1460px] grid-cols-[repeat(4,minmax(172px,1fr))_repeat(3,minmax(264px,1.35fr))] gap-3">
            <Card class="kpi-card w-full" :class="cardSurfaceClass('all', 'status')" :style="cardThemeStyle('all')" noSpace>
              <div class="kpi-panel cursor-pointer p-4" @click="applyCardFilter('status', 'all')">
                <div class="kpi-topline">
                  <div class="kpi-icon">
                    <i class="fa-solid fa-file-invoice-dollar"></i>
                  </div>
                  <div class="kpi-value">
                    {{ Number(meta?.summary?.totalOrders || 0) }}
                  </div>
                </div>
                <div class="kpi-label-group">
                  <h2 class="kpi-title">Pedidos do período</h2>
                </div>
              </div>
            </Card>

            <Card class="kpi-card w-full" :class="cardSurfaceClass('billed', 'status')" :style="cardThemeStyle('billed')" noSpace>
              <div class="kpi-panel cursor-pointer p-4" @click="applyCardFilter('status', 'billed')">
                <div class="kpi-topline">
                  <div class="kpi-icon">
                    <i class="fa-solid fa-receipt"></i>
                  </div>
                  <div class="kpi-value">
                    {{ Number(meta?.summary?.billedCount || 0) }}
                  </div>
                </div>
                <div class="kpi-label-group">
                  <h2 class="kpi-title">Faturados</h2>
                </div>
              </div>
            </Card>

            <Card class="kpi-card w-full" :class="cardSurfaceClass('notReceived', 'status')" :style="cardThemeStyle('notReceived')" noSpace>
              <div class="kpi-panel cursor-pointer p-4" @click="applyCardFilter('status', 'notReceived')">
                <div class="kpi-topline">
                  <div class="kpi-icon">
                    <i class="fa-solid fa-wallet"></i>
                  </div>
                  <div class="kpi-value">
                    {{ Number(meta?.summary?.notReceivedCount || 0) }}
                  </div>
                </div>
                <div class="kpi-label-group">
                  <h2 class="kpi-title">Ainda não recebidos</h2>
                </div>
              </div>
            </Card>

            <Card class="kpi-card w-full" :class="cardSurfaceClass('received', 'status')" :style="cardThemeStyle('received')" noSpace>
              <div class="kpi-panel cursor-pointer p-4" @click="applyCardFilter('status', 'received')">
                <div class="kpi-topline">
                  <div class="kpi-icon">
                    <i class="fa-solid fa-circle-check"></i>
                  </div>
                  <div class="kpi-value">
                    {{ Number(meta?.summary?.receivedCount || 0) }}
                  </div>
                </div>
                <div class="kpi-label-group">
                  <h2 class="kpi-title">Recebidos</h2>
                </div>
              </div>
            </Card>

            <Card
              v-for="card in profitabilityCards(meta?.summary)"
              :key="card.key"
              class="kpi-card kpi-card--small w-full"
              :class="cardSurfaceClass(card.key, 'profitability')"
              :style="cardThemeStyle(card.theme)"
              noSpace>
              <div class="kpi-panel kpi-panel--small cursor-pointer p-4" @click="applyCardFilter('profitability', card.key)">
                <div class="kpi-topline">
                  <div class="kpi-icon">
                    <i :class="card.icon"></i>
                  </div>
                  <div class="kpi-value kpi-value--small">
                    {{ formatMoney(card.value) }}
                  </div>
                </div>
                <div class="kpi-label-group">
                  <h2 class="kpi-title">{{ card.label }}</h2>
                  <div class="kpi-subtitle">{{ card.subtitle }}</div>
                  <div v-if="card.secondaryLabel" class="kpi-subtitle">{{ card.secondaryLabel }}</div>
                </div>
              </div>
            </Card>
          </div>
        </div>

        <div class="overflow-x-auto pb-1">
          <div class="grid min-w-[920px] grid-cols-5 gap-3">
            <Card
              v-for="card in classificationCards(meta?.summary, items)"
              :key="card.key"
              class="kpi-card kpi-card--small w-full"
              :class="cardSurfaceClass(card.key, 'classification')"
              :style="cardThemeStyle(card.theme)"
              noSpace>
              <div class="kpi-panel kpi-panel--small cursor-pointer p-4" @click="applyCardFilter('classification', card.key)">
                <div class="kpi-topline">
                  <div class="kpi-icon">
                    <i :class="card.icon"></i>
                  </div>
                  <div class="kpi-value kpi-value--small">
                    {{ formatMoney(card.value) }}
                  </div>
                </div>
                <div class="kpi-label-group kpi-label-group--split">
                  <div>
                    <h2 class="kpi-title">{{ card.label }}</h2>
                    <div class="kpi-subtitle">{{ Number(card.count || 0) }} pedido(s)</div>
                  </div>

                  <div class="kpi-mini-stack">
                    <div class="kpi-mini-row">
                      <span class="kpi-mini-label">Produto</span>
                      <span class="kpi-mini-value">{{ formatMoney(card.productValue) }}</span>
                    </div>
                    <div class="kpi-mini-row">
                      <span class="kpi-mini-label">Serviço</span>
                      <span class="kpi-mini-value">{{ formatMoney(card.serviceValue) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </template>
    <template #orderName="{item}">
      <div class="text-wrap text-[12px] font-medium leading-4 text-[#13161b]">{{ item.orderName || "-" }}</div>
      <div class="text-[11px] leading-4 text-slate-500">{{ item.orderNumber || "-" }}</div>
    </template>

    <template #clientName="{item}">
      <div class="text-wrap text-[12px] leading-4 text-[#13161b]">{{ item.clientName || "-" }}</div>
      <div class="text-[11px] leading-4 text-slate-500">{{ item.clientDocument || "-" }}</div>
    </template>

    <template #distributorName="{item}">
      <span class="text-wrap text-[12px] leading-4 text-slate-700">{{ item.distributorName || "-" }}</span>
    </template>

    <template #quoteNumber="{item}">
      <span class="text-[12px] leading-4 text-slate-700">{{ item.quoteNumber || "-" }}</span>
    </template>

    <template #proposalClassificationName="{item}">
      <span class="text-[12px] leading-4 text-slate-700">{{ item.proposalClassificationName || "-" }}</span>
    </template>

    <template #createdOn="{item}">
      <span class="text-[12px] leading-4 text-slate-700">{{ formatDateTime(item.createdOn) || "-" }}</span>
    </template>

    <template #billingDate="{item}">
      <span class="text-[12px] leading-4 text-slate-700">{{ formatDateTime(item.billingDate) || "-" }}</span>
    </template>

    <template #orderProductKind="{item}">
      <span class="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold leading-4" :class="productKindBadgeClass(item.orderProductKind)">
        {{ item.orderProductKind || "-" }}
      </span>
    </template>

    <template #totalAmount="{item}">
      <span class="text-[12px] font-medium leading-4 text-[#13161b]">{{ formatMoney(item.totalAmount) }}</span>
    </template>

    <template #budgetedProfitability="{item}">
      <span class="text-[12px] font-medium leading-4 text-[#13161b]">{{ formatMoney(item.budgetedProfitability) }}</span>
    </template>

    <template #realizedProfitability="{item}">
      <span class="text-[12px] font-medium leading-4 text-[#13161b]">{{ formatMoney(item.realizedProfitability) }}</span>
    </template>

    <template #financialFlow="{item}">
      <div class="min-w-[260px]">
        <div class="flow-combo">
          <span v-for="step in item.progressSteps" :key="step.key" class="flow-combo__item" :class="flowStepClass(step)">
            {{ step.label }}
          </span>
        </div>
      </div>
    </template>

    <template #statusLabel="{item}">
      <span class="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold leading-4" :class="statusBadgeClass(item.statusKey)">
        {{ item.statusLabel || "-" }}
      </span>
    </template>

    <template #actions="{item}">
      <div class="flex items-center justify-center gap-2 text-[#475569]">
        <TheToolTip label="Ver pedido">
          <button type="button" class="grid h-8 w-8 place-items-center rounded-md transition hover:bg-slate-100 hover:text-[#13161b]" @click.stop="openOrderDetail(item)">
            <i class="fa-regular fa-eye text-[14px]" />
          </button>
        </TheToolTip>
      </div>
    </template>
  </ManageShell>

  <Transition name="drawer-fade">
    <div v-if="detailDrawer.open" class="fixed inset-0 z-40 bg-slate-900/30" @click="closeOrderDetail"></div>
  </Transition>

  <Transition name="drawer-slide">
    <aside v-if="detailDrawer.open" class="fixed right-0 top-0 z-50 h-screen w-full max-w-[840px] border-l border-slate-200 bg-white shadow-[-12px_0_40px_rgba(15,23,42,0.12)]">
      <div class="flex h-full flex-col">
        <div class="border-b border-slate-200 px-5 pb-4 pt-5">
          <div class="flex items-start justify-between gap-3">
            <div>
              <div class="text-[30px] font-semibold tracking-[-0.5px] text-[#13161b]">
                {{ detailDrawer.data?.header?.title || "Pedido CRM" }}
              </div>
            </div>

            <button type="button" class="grid h-8 w-8 place-items-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-700" @click="closeOrderDetail">
              <i class="fa-solid fa-xmark text-[16px]"></i>
            </button>
          </div>

          <div class="mt-4 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold" :class="statusBadgeClass(detailDrawer.data?.header?.paymentStatus)">
            {{ detailDrawer.data?.header?.statusLabel || "-" }}
          </div>
        </div>

        <div class="grow overflow-y-auto px-5 py-4">
          <TheLoader v-if="detailDrawer.loading" />

          <div v-else-if="detailDrawer.data" class="space-y-5">
            <div class="grid gap-3 border-b border-slate-200 pb-5 text-[14px] text-slate-700">
              <div class="detail-row">
                <span class="detail-label"><i class="fa-regular fa-circle text-[8px]"></i>Status</span>
                <span>{{ detailDrawer.data.summary.statusLabel || "-" }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label"><i class="fa-regular fa-calendar"></i>Ultima Atualizacao</span>
                <span>{{ formatDateTime(detailDrawer.data.summary.creationDate) || "-" }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label"><i class="fa-regular fa-building"></i>Cliente</span>
                <span>{{ detailDrawer.data.summary.clientName || "-" }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label"><i class="fa-regular fa-user"></i>Contato</span>
                <span class="text-right">{{ formatContact(detailDrawer.data.summary) }}</span>
              </div>
            </div>

            <div class="grid gap-3 border-b border-slate-200 pb-5 text-[14px] text-slate-700">
              <div class="detail-row">
                <span class="detail-label"><i class="fa-regular fa-file-lines"></i>ID do Pedido</span>
                <span>{{ detailDrawer.data.summary.orderNumber || "-" }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label"><i class="fa-regular fa-truck"></i>Fornecedor</span>
                <span>{{ detailDrawer.data.summary.supplierName || "-" }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label"><i class="fa-regular fa-calendar-days"></i>Data do Pedido</span>
                <span>{{ formatDateTime(detailDrawer.data.summary.orderDate) || "-" }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label"><i class="fa-regular fa-clock"></i>Previsao de Entrega</span>
                <span>{{ formatDateTime(detailDrawer.data.summary.deliveryForecast) || "-" }}</span>
              </div>
              <div class="detail-row items-start">
                <span class="detail-label"><i class="fa-regular fa-user-group"></i>Envolvidos</span>
                <div class="flex flex-wrap justify-end gap-2">
                  <div v-for="person in detailDrawer.data.summary.involved" :key="`${person.type}-${person.name}`" class="inline-flex items-center gap-2 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700">
                    <span class="grid h-6 w-6 place-items-center rounded-full bg-[#0377cb] text-[10px] font-semibold text-white">{{ person.initials }}</span>
                    <span>{{ person.name }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="rounded-[10px] bg-sky-100 px-4 py-3 text-[14px] text-slate-700">
              <div class="font-semibold text-[#13161b]">Descricao:</div>
              <div class="mt-1 whitespace-pre-line">{{ detailDrawer.data.summary.description || "Sem descricao informada." }}</div>
            </div>

            <div>
              <div class="flex flex-wrap gap-5 border-b border-slate-200">
                <button
                  v-for="tab in detailTabs"
                  :key="tab.value"
                  type="button"
                  class="border-b-2 pb-2 text-[14px] transition"
                  :class="detailDrawer.activeTab === tab.value ? 'border-[#0b78d1] font-semibold text-[#0b78d1]' : 'border-transparent text-slate-600 hover:text-slate-800'"
                  @click="detailDrawer.activeTab = tab.value"
                >
                  {{ tab.label }}
                </button>
              </div>

              <div class="pt-4">
                <div v-if="detailDrawer.activeTab === 'values'">
                  <div class="overflow-hidden rounded-[10px] border border-slate-200 bg-white">
                    <div class="grid grid-cols-[1.5fr_1fr] bg-slate-50 px-3 py-2 text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                      <div>Campo</div>
                      <div class="text-right">Valor</div>
                    </div>
                    <div v-for="row in detailDrawer.data.values" :key="row.label" class="grid grid-cols-[1.5fr_1fr] border-t border-slate-200 px-3 py-2 text-[13px]">
                      <div class="text-slate-600">{{ row.label }}</div>
                      <div class="text-right font-medium text-[#13161b]">
                        {{ formatDetailValue(row.value, row.label.includes("(%)") ? "percent" : "money") }}
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else-if="detailDrawer.activeTab === 'products'">
                  <div class="overflow-hidden rounded-[10px] border border-slate-200 bg-white">
                    <div class="grid grid-cols-[1.6fr_1fr_0.7fr_1fr] bg-slate-50 px-3 py-2 text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                      <div>Produto</div>
                      <div>PRO</div>
                      <div class="text-center">Qtd</div>
                      <div class="text-right">Valor Total</div>
                    </div>

                    <div v-if="detailDrawer.data.products?.length">
                      <div v-for="product in detailDrawer.data.products" :key="`${product.item}-${product.pro}`" class="grid grid-cols-[1.6fr_1fr_0.7fr_1fr] border-t border-slate-200 px-3 py-2 text-[13px]">
                        <div class="pr-3 font-medium text-[#13161b]">{{ product.item }}</div>
                        <div class="text-slate-600">{{ product.pro }}</div>
                        <div class="text-center text-slate-600">{{ product.quantity }}</div>
                        <div class="text-right font-medium text-[#13161b]">{{ formatMoney(product.totalValue) }}</div>
                      </div>
                    </div>

                    <div v-else class="px-4 py-5 text-sm text-slate-500">Os itens do pedido ainda nÃ£o estao sincronizados localmente.</div>
                  </div>
                </div>

                <div v-else-if="detailDrawer.activeTab === 'timeline'" class="space-y-3">
                  <div v-for="row in detailDrawer.data.timeline" :key="`${row.title}-${row.date}`" class="flex gap-3 rounded-[10px] border border-slate-200 px-4 py-3">
                    <div class="mt-1 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-sky-100 text-[#0377cb]">
                      <i class="fa-regular fa-clock text-[14px]"></i>
                    </div>
                    <div class="min-w-0 flex-1">
                      <div class="text-[14px] font-semibold text-[#13161b]">{{ row.title }}</div>
                      <div class="mt-1 text-[13px] text-slate-500">{{ row.subtitle }}</div>
                      <div class="mt-1 text-[12px] text-slate-400">{{ formatDateTime(row.date) || "-" }}</div>
                    </div>
                  </div>
                </div>

                <div v-else>
                  <div class="overflow-hidden rounded-[10px] border border-slate-200 bg-white">
                    <div class="grid grid-cols-[0.7fr_1fr_1fr_0.9fr] bg-slate-50 px-3 py-2 text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                      <div>Parcela</div>
                      <div>Vencimento</div>
                      <div class="text-right">Valor a Receber</div>
                      <div class="text-right">Status</div>
                    </div>

                    <div v-for="row in detailDrawer.data.paymentRows" :key="row.parcelaId" class="grid grid-cols-[0.7fr_1fr_1fr_0.9fr] border-t border-slate-200 px-3 py-2 text-[13px]">
                      <div class="font-medium text-[#13161b]">{{ row.parcela }}</div>
                      <div class="text-slate-600">{{ formatDate(row.vencimento) || "-" }}</div>
                      <div class="text-right font-medium text-[#13161b]">{{ formatMoney(row.valor) }}</div>
                      <div class="flex justify-end">
                        <span class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold" :class="statusBadgeClass(row.paymentStatus)">
                          {{ row.status }}
                        </span>
                      </div>
                    </div>

                    <div v-if="!detailDrawer.data.paymentRows?.length" class="px-4 py-5 text-sm text-slate-500">Nenhuma parcela encontrada para este pedido.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </aside>
  </Transition>
</template>

<script setup>
import {defineProps, ref} from "vue"
import {useStore} from "vuex"
import {get} from "@/crud"
import {useFormat} from "@/composables/useFormat"
import ProgressBar from "@/components/animation/ProgressBar.vue"

const props = defineProps({
  api: {type: String, required: true},
  scope: {type: String, required: true},
  screenName: {type: String, default: "Pedidos Realizados"},
  screenSubtitle: {type: String, default: "Pedidos faturados filtrados pela data de criação do pedido."},
  includeSellerFilter: {type: Boolean, default: false},
  linkedSellerCaption: {type: String, default: "vinculada ao seu usuario"},
})

const store = useStore()
const {formatDate} = useFormat()

const detailTabs = [
  {label: "Valores", value: "values"},
  {label: "Produtos", value: "products"},
  {label: "Linha do Tempo", value: "timeline"},
  {label: "Pagamento", value: "payment"},
]

const detailDrawer = ref({
  open: false,
  loading: false,
  activeTab: "values",
  data: null,
})
const selectedGoalId = ref(null)

const getCurrentMonthRange = () => {
  const now = new Date()
  const startDate = new Date(now.getFullYear(), now.getMonth(), 1)
  const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0)

  startDate.setHours(0, 0, 0, 0)
  endDate.setHours(23, 59, 59, 999)

  const pad = (value) => String(value).padStart(2, "0")
  const toSqlDateTime = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`

  return [toSqlDateTime(startDate), toSqlDateTime(endDate)]
}

const initialHeaders = [
  {title: "PRO", field: "quoteNumber", itemClass: "w-[140px]"},
  {title: "Classificação", field: "proposalClassificationName", itemClass: "w-[180px]"},
  {title: "Cliente", field: "clientName", itemClass: "max-w-[220px] text-wrap"},
  {title: "Pedido", field: "orderName", itemClass: "min-w-[320px] max-w-[420px] text-wrap"},
  {title: "Distribuidor", field: "distributorName", itemClass: "max-w-[200px] text-wrap"},
  {title: "Criacao", field: "createdOn", itemClass: "w-[170px]"},
  {title: "Faturamento", field: "billingDate", itemClass: "w-[170px]"},
  {title: "Tipo", field: "orderProductKind", itemClass: "w-[130px]"},
  {title: "Valor", field: "totalAmount", itemClass: "w-[160px]"},
  {title: "Rent. Orçada", field: "budgetedProfitability", itemClass: "w-[170px]"},
  {title: "Rent. Realizada", field: "realizedProfitability", itemClass: "w-[170px]"},
  {title: "Fluxo Financeiro", field: "financialFlow", itemClass: "min-w-[320px]"},
]

const allHeaders = [...initialHeaders, {title: "Oportunidade", field: "opportunityName"}]

const buildFilters = () => {
  const filters = [
    {
      label: "Pesquisa",
      param: "search",
      operator: "like",
      type: "text",
      placeholder: "Cliente, pedido, oportunidade ou PRO",
    },
    {
      label: "Periodo de criação",
      param: "createdPeriod",
      operator: "between",
      type: "range",
      placeholder: "Selecione o período",
    },
  ]

  if (props.includeSellerFilter) {
    filters.unshift({
      label: "Vendedora",
      param: "sellerId",
      operator: "=",
      type: "zoom",
      database: "crm_seller_user",
      itemText: "sellerName",
      itemValue: "systemuserid",
      itemAutocomplete: ["sellerName", "sellerEmail", "loginEmail"],
      placeholder: "Selecione a vendedora",
    })
  }

  return filters
}

const dataControl = ref({
  api: props.api,
  screenName: props.screenName,
  screenSubtitle: props.screenSubtitle,
  tableAreaMinHeight: '720px',
  headers: initialHeaders,
  allHeaders,
  filters: buildFilters(),
  limit: 5000,
  persistLimit: false,
  tableDensity: 'ultra',
  filter: [
    {
      column: "createdPeriod",
      operator: "between",
      value: getCurrentMonthRange(),
    },
  ],
  order: [{direction: "DESC", column: "createdOn", name: "Criacao do Pedido"}],
  scope: props.scope,
  clientFilterState: {
    status: "all",
    profitability: "total",
    classification: "all",
  },
  clientFilterFn: null,
})

const defaultFilterState = () => ({
  status: "all",
  profitability: "total",
  classification: "all",
})

const buildClientFilterFn = (state = defaultFilterState()) => {
  const current = {
    ...defaultFilterState(),
    ...(state || {}),
  }

  return (item) => {
    if (current.status === "billed" && !item?.progress?.billed) return false
    if (current.status === "received" && !item?.progress?.received) return false
    if (current.status === "notReceived" && item?.progress?.received) return false

    if (current.profitability === "products" && !isProductOrderKind(item?.orderProductKind)) return false
    if (current.profitability === "services" && !isServiceOrderKind(item?.orderProductKind)) return false

    if (current.classification !== "all" && proposalClassificationBucket(item?.proposalClassificationName) !== current.classification) return false

    return true
  }
}

const applyCardFilter = (category, value) => {
  const currentState = {
    ...defaultFilterState(),
    ...(dataControl.value.clientFilterState || {}),
  }
  const resetValue = category === "profitability" ? "total" : "all"
  const nextValue = currentState[category] === value ? resetValue : value
  const nextState = {
    ...currentState,
    [category]: nextValue,
  }

  dataControl.value = {
    ...dataControl.value,
    clientFilterState: nextState,
    clientFilterFn: buildClientFilterFn(nextState),
  }
}

const cardSurfaceClass = (cardKey, category) => {
  const active = dataControl.value.clientFilterState?.[category] === cardKey
  return active ? "is-active -translate-y-0.5 shadow-sm" : "hover:-translate-y-0.5 hover:shadow-sm"
}

const cardThemeMap = {
  periodValue: {
    color: "#0ea5e9",
    soft: "#e0f2fe",
    icon: "#0284c7",
    text: "#ffffff",
  },
  all: {
    color: "#22c55e",
    soft: "#dcfce7",
    icon: "#16a34a",
    text: "#ffffff",
  },
  billed: {
    color: "#f59e0b",
    soft: "#fef3c7",
    icon: "#d97706",
    text: "#ffffff",
  },
  notReceived: {
    color: "#f97316",
    soft: "#ffedd5",
    icon: "#ea580c",
    text: "#ffffff",
  },
  received: {
    color: "#06b6d4",
    soft: "#cffafe",
    icon: "#0891b2",
    text: "#ffffff",
  },
  profitability: {
    color: "#8b5cf6",
    soft: "#ede9fe",
    icon: "#7c3aed",
    text: "#ffffff",
  },
  classificationNew: {
    color: "#3b82f6",
    soft: "#dbeafe",
    icon: "#2563eb",
    text: "#ffffff",
  },
  classificationAddition: {
    color: "#14b8a6",
    soft: "#ccfbf1",
    icon: "#0d9488",
    text: "#ffffff",
  },
  classificationRenewal: {
    color: "#eab308",
    soft: "#fef3c7",
    icon: "#ca8a04",
    text: "#ffffff",
  },
  classificationRecurrence: {
    color: "#a855f7",
    soft: "#ede9fe",
    icon: "#9333ea",
    text: "#ffffff",
  },
  classificationOthers: {
    color: "#64748b",
    soft: "#e2e8f0",
    icon: "#475569",
    text: "#ffffff",
  },
}

const cardThemeStyle = (cardKey) => {
  const theme = cardThemeMap[cardKey] || cardThemeMap.all
  const filterState = dataControl.value.clientFilterState || {}
  const active =
    filterState.status === cardKey ||
    filterState.profitability === cardKey ||
    filterState.classification === cardKey

  return {
    "--kpi-accent": theme.color,
    "--kpi-soft": active ? "rgba(255,255,255,0.18)" : theme.soft,
    "--kpi-icon-color": active ? theme.text : theme.icon,
    "--kpi-text": active ? theme.text : "#13161b",
    "--kpi-muted": active ? theme.text : "#64748b",
    "--kpi-border": active ? theme.color : "#e2e8f0",
    "--kpi-bg": active ? theme.color : "#ffffff",
  }
}

const formatMoney = (value) => {
  return Number(value || 0).toLocaleString("pt-BR", {style: "currency", currency: "BRL"})
}

const toSafeNumber = (value) => {
  const normalized = Number(value || 0)
  return Number.isFinite(normalized) ? normalized : 0
}

const proposalClassificationBucket = (value) => {
  const normalized = String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()

  if (!normalized) return "others"

  const compact = normalized.replace(/\s*-\s*/g, "-").replace(/\s+/g, " ")
  const hasNew = compact.includes("novo") || compact.includes("nova")
  const hasAddition = compact.includes("adicao")
  const hasRenewal = compact.includes("renovacao")
  const hasRecurrence = compact.includes("recorrencia")

  if (hasNew) return "new"
  if (hasAddition) return "addition"
  if (hasRenewal) return "renewal"
  if (hasRecurrence) return "recurrence"

  return "others"
}

const normalizeOrderKind = (value) => {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
}

const isProductOrderKind = (value) => normalizeOrderKind(value) === "produto"
const isServiceOrderKind = (value) => normalizeOrderKind(value) === "servico"

const profitabilityCards = (summary = {}) => [
  {
    key: "total",
    label: "Rentabilidade Realizada",
    subtitle: "Todos os pedidos do filtro atual",
    secondaryLabel: `Orçada: ${formatMoney(summary?.totalBudgetedProfitability || 0)}`,
    value: summary?.totalRealizedProfitability || summary?.totalProfitability || 0,
    theme: "profitability",
    icon: "fa-solid fa-chart-line",
  },
  {
    key: "products",
    label: "Realizada Produtos",
    subtitle: `${Number(summary?.productCount || 0)} pedido(s) de produto`,
    secondaryLabel: `Orçada: ${formatMoney(summary?.productBudgetedProfitability || 0)}`,
    value: summary?.productRealizedProfitability || summary?.productProfitability || 0,
    theme: "classificationNew",
    icon: "fa-solid fa-box-open",
  },
  {
    key: "services",
    label: "Realizada Serviços",
    subtitle: `${Number(summary?.serviceCount || 0)} pedido(s) de serviço`,
    secondaryLabel: `Orçada: ${formatMoney(summary?.serviceBudgetedProfitability || 0)}`,
    value: summary?.serviceRealizedProfitability || summary?.serviceProfitability || 0,
    theme: "classificationAddition",
    icon: "fa-solid fa-screwdriver-wrench",
  },
]

const buildClassificationKindBreakdown = (items = []) => {
  return (Array.isArray(items) ? items : []).reduce((acc, item) => {
    const bucket = proposalClassificationBucket(item?.proposalClassificationName)
    const profitabilityValue = toSafeNumber(item?.profitability)

    if (!acc[bucket]) {
      acc[bucket] = {productValue: 0, serviceValue: 0}
    }

    if (isServiceOrderKind(item?.orderProductKind)) {
      acc[bucket].serviceValue += profitabilityValue
    } else if (isProductOrderKind(item?.orderProductKind)) {
      acc[bucket].productValue += profitabilityValue
    }

    return acc
  }, {})
}

const classificationCards = (summary = {}, items = []) => {
  const profitability = summary?.profitabilityByClassification || {}
  const orders = summary?.ordersByClassification || {}
  const kindBreakdown = buildClassificationKindBreakdown(items)

  return [
    { key: 'new', label: 'Novo', value: profitability.new || 0, count: orders.new || 0, productValue: kindBreakdown.new?.productValue || 0, serviceValue: kindBreakdown.new?.serviceValue || 0, theme: 'classificationNew', icon: 'fa-solid fa-star' },
    { key: 'addition', label: 'Adição', value: profitability.addition || 0, count: orders.addition || 0, productValue: kindBreakdown.addition?.productValue || 0, serviceValue: kindBreakdown.addition?.serviceValue || 0, theme: 'classificationAddition', icon: 'fa-solid fa-plus' },
    { key: 'renewal', label: 'Renovação', value: profitability.renewal || 0, count: orders.renewal || 0, productValue: kindBreakdown.renewal?.productValue || 0, serviceValue: kindBreakdown.renewal?.serviceValue || 0, theme: 'classificationRenewal', icon: 'fa-solid fa-rotate' },
    { key: 'recurrence', label: 'Recorrência', value: profitability.recurrence || 0, count: orders.recurrence || 0, productValue: kindBreakdown.recurrence?.productValue || 0, serviceValue: kindBreakdown.recurrence?.serviceValue || 0, theme: 'classificationRecurrence', icon: 'fa-solid fa-arrows-rotate' },
    { key: 'others', label: 'Outros', value: profitability.others || 0, count: orders.others || 0, productValue: kindBreakdown.others?.productValue || 0, serviceValue: kindBreakdown.others?.serviceValue || 0, theme: 'classificationOthers', icon: 'fa-solid fa-layer-group' },
  ]
}

const goalSelectItems = (goals = []) => {
  return (Array.isArray(goals) ? goals : []).map((goal) => ({
    value: goal.goalid || goal.id,
    label: buildGoalOptionLabel(goal),
  }))
}

const buildGoalOptionLabel = (goal = {}) => {
  const title = goal.metricName || goal.title || "Meta"
  const scope = goal?.profitabilityScopeLabel ? ` - ${goal.profitabilityScopeLabel}` : ""
  return `${title}${scope}`
}

const activeGoal = (goals = []) => {
  const list = Array.isArray(goals) ? goals : []
  const selectedId = String(selectedGoalId.value || "").trim()

  if (!selectedId) {
    return list[0] || null
  }

  return list.find((goal) => String(goal.goalid || goal.id) === selectedId) || list[0] || null
}

const selectedGoalModel = (goals = []) => {
  return activeGoal(goals)?.goalid || activeGoal(goals)?.id || null
}

const goalRuleSummary = (goal = null) => {
  if (!goal) {
    return "Nenhuma meta vigente encontrada para o período selecionado."
  }

  const profitabilityScopeLabel = goal.profitabilityScopeLabel || "Total"

  if (goal.classificationMode === "selected" && Array.isArray(goal.classificationLabels) && goal.classificationLabels.length) {
    return `${profitabilityScopeLabel} • ${goal.classificationLabels.join(", ")}`
  }

  return `${profitabilityScopeLabel} • Todas as classificações`
}

const isGoalCompleted = (goal = null) => Number(goal?.percentage || 0) >= 100

const formatGoalPercent = (value) => {
  const percent = Number(value || 0)
  return `${percent.toLocaleString("pt-BR", {maximumFractionDigits: 1, minimumFractionDigits: percent % 1 ? 1 : 0})}%`
}

const goalProgressValue = (goal = null) => {
  return Math.max(0, Number(goal?.actualMoney || 0))
}

const goalProgressMax = (goal = null) => {
  const target = Number(goal?.targetMoney || 0)
  return target > 0 ? target : 100
}

const goalProgressPercent = (goal = null) => {
  const max = goalProgressMax(goal)
  if (!max) return 0
  return (goalProgressValue(goal) / max) * 100
}

const goalProgressColor = (goal = null) => {
  const percent = goalProgressPercent(goal)

  if (percent >= 100) return "#22c55e"
  if (percent >= 60) return "#0ea5e9"
  if (percent >= 10) return "#facc15"
  return "#ef4444"
}

const goalProgressKey = (goal = null) => {
  if (!goal) {
    return "goal-progress-empty"
  }

  return `${goal.goalid || goal.id || "goal"}-${goalProgressValue(goal)}-${goalProgressMax(goal)}`
}

const formatDateTime = (value) => {
  if (!value) return ""
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value))
}

const formatContact = (summary = {}) => {
  return [summary.contactName, summary.contactPhone, summary.contactEmail].filter(Boolean).join(" | ")
}

const formatDetailValue = (value, type = "money") => {
  if (type === "percent") {
    return `${Number(value || 0).toLocaleString("pt-BR", {minimumFractionDigits: 0, maximumFractionDigits: 2})}%`
  }
  return formatMoney(value)
}

const statusBadgeClass = (status) => {
  if (status === "paid") return "bg-emerald-100 text-emerald-700"
  if (status === "received") return "bg-sky-100 text-sky-700"
  if (status === "distributor") return "bg-amber-100 text-amber-700"
  if (status === "billed") return "bg-violet-100 text-violet-700"
  return "bg-slate-100 text-slate-600"
}

const flowStepClass = (currentStep = {}) => {
  if (currentStep.done) {
    return "bg-emerald-500 text-white border-r border-emerald-600"
  }

  if (currentStep.error || currentStep.status === "error") {
    return "bg-rose-500 text-white border-r border-rose-600"
  }

  return "bg-slate-100 text-slate-500 border-r border-slate-300"
}

const productKindBadgeClass = (value) => {
  const normalized = String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()

  if (normalized === "servico") {
    return "bg-amber-100 text-amber-800"
  }

  if (normalized === "produto") {
    return "bg-slate-200 text-slate-800"
  }

  return "bg-slate-100 text-slate-500"
}

const closeOrderDetail = () => {
  detailDrawer.value.open = false
}

const openOrderDetail = async (item) => {
  if (!item?.salesorderId) return

  detailDrawer.value.open = true
  detailDrawer.value.loading = true
  detailDrawer.value.activeTab = "values"

  try {
    const params = {
      salesorderId: item.salesorderId,
    }

    if (item?.sellerId) {
      params.sellerId = item.sellerId
    }

    const response = await get("dashboard_me/order_detail", {params})

    if (response?.error) {
      throw new Error(response?.data?.[0]?.message || "Erro ao carregar detalhes do pedido.")
    }

    detailDrawer.value.data = response
  } catch (error) {
    detailDrawer.value.open = false
    store.dispatch("setError", {
      message: error.message || "Erro ao carregar detalhes do pedido.",
    })
  } finally {
    detailDrawer.value.loading = false
  }
}
</script>

<style scoped>
.kpi-card {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid var(--kpi-border, #e2e8f0);
  background: var(--kpi-bg, #ffffff);
  transition: all 0.2s ease;
}

.goal-panel-caption {
  font-size: 0.78rem;
  line-height: 1rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 8px;
}

.goal-panel-title {
  font-size: 1rem;
  line-height: 1.25rem;
  font-weight: 700;
  color: #13161b;
}

.goal-panel-subtitle {
  margin-top: 4px;
  font-size: 0.8rem;
  line-height: 1rem;
  color: #64748b;
}

.goal-panel-percent {
  font-size: 1.4rem;
  line-height: 1.2;
  font-weight: 700;
  color: #13161b;
}

.goal-hit-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  border-radius: 999px;
  background: #dcfce7;
  color: #15803d;
  padding: 4px 10px;
  font-size: 0.72rem;
  line-height: 1rem;
  font-weight: 800;
}

.goal-panel-values {
  margin-top: 4px;
  font-size: 0.8rem;
  line-height: 1rem;
  color: #64748b;
}

.kpi-panel {
  min-height: 124px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  color: var(--kpi-text, #13161b);
}

.kpi-card--small {
  border-radius: 14px;
}

.kpi-panel--small {
  min-height: 98px;
}

.kpi-topline {
  display: flex;
  align-items: center;
  gap: 12px;
}

.kpi-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  font-size: 0.95rem;
  background: var(--kpi-soft, #f8fafc);
  color: var(--kpi-icon-color, #13161b);
  transition: inherit;
}

.kpi-value {
  font-size: 1.5rem;
  line-height: 1.15;
  font-weight: 700;
  color: var(--kpi-text, #13161b);
  word-break: break-word;
}

.kpi-value--small {
  font-size: 1.15rem;
}

.kpi-label-group {
  margin-top: 16px;
}

.kpi-label-group--split {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
}

.kpi-title {
  font-size: 0.95rem;
  line-height: 1.2rem;
  color: var(--kpi-text, #13161b);
  font-weight: 600;
}

.kpi-subtitle {
  margin-top: 4px;
  font-size: 0.78rem;
  line-height: 1rem;
  color: var(--kpi-muted, #64748b);
}

.kpi-mini-stack {
  min-width: 108px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.kpi-mini-row {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
}

.kpi-mini-label {
  font-size: 0.68rem;
  line-height: 0.9rem;
  color: var(--kpi-muted, #64748b);
}

.kpi-mini-value {
  font-size: 0.72rem;
  line-height: 0.95rem;
  font-weight: 700;
  color: var(--kpi-text, #13161b);
}

.kpi-card.is-active {
  border-color: var(--kpi-accent, #0f172a);
  background: var(--kpi-accent, #0f172a);
}

.kpi-card.is-active .kpi-panel,
.kpi-card.is-active .kpi-value,
.kpi-card.is-active .kpi-title,
.kpi-card.is-active .kpi-subtitle,
.kpi-card.is-active .kpi-mini-label,
.kpi-card.is-active .kpi-mini-value,
.kpi-card.is-active .kpi-icon,
.kpi-card.is-active i {
  color: #ffffff !important;
}

.kpi-card.is-active .kpi-icon {
  background: rgba(255, 255, 255, 0.18) !important;
}

.flow-combo {
  display: inline-flex;
  flex-wrap: wrap;
  overflow: hidden;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  background: #f8fafc;
}

.flow-combo__item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  padding: 4px 10px;
  font-size: 11px;
  line-height: 1;
  font-weight: 700;
  white-space: nowrap;
  border-right: 1px solid #cbd5e1;
}

.flow-combo__item:last-child {
  border-right: none;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.detail-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #475569;
}

.drawer-fade-enter-active,
.drawer-fade-leave-active,
.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: all 0.22s ease;
}

.drawer-fade-enter-from,
.drawer-fade-leave-to {
  opacity: 0;
}

.drawer-slide-enter-from,
.drawer-slide-leave-to {
  transform: translateX(100%);
}
</style>


